import {
  Entitlement,
  EntitlementMetadata,
  Subscription,
} from './billing-subscription';

/**
 * Capability keys emitted by the backend on `/entitlements` (`capabilities` /
 * `limits`) and on subscription entitlements (`metadata.feature_key`).
 *
 * Note: `feature_key` is not unique — several entitlements can share one key
 * (e.g. the three API call-limit rows all map to `API_ACCESS`).
 */
export enum PlanCapability {
  FILTER_PEOPLE_BASIC_SEARCH = 'FILTER_PEOPLE_BASIC_SEARCH',
  FILTER_PEOPLE_BASIC_PEOPLE_INFO = 'FILTER_PEOPLE_BASIC_PEOPLE_INFO',
  FILTER_PEOPLE_BASIC_JOB_ROLE = 'FILTER_PEOPLE_BASIC_JOB_ROLE',
  FILTER_PEOPLE_BASIC_JOB_CHANGE = 'FILTER_PEOPLE_BASIC_JOB_CHANGE',
  FILTER_PEOPLE_BASIC_LOCATION = 'FILTER_PEOPLE_BASIC_LOCATION',
  FILTER_PEOPLE_BASIC_INDUSTRY = 'FILTER_PEOPLE_BASIC_INDUSTRY',
  FILTER_PEOPLE_BASIC_EMPLOYEE_SIZE = 'FILTER_PEOPLE_BASIC_EMPLOYEE_SIZE',
  FILTER_PEOPLE_BASIC_COMPANY_TYPE = 'FILTER_PEOPLE_BASIC_COMPANY_TYPE',
  FILTER_PEOPLE_BASIC_SIC_AND_NAICS = 'FILTER_PEOPLE_BASIC_SIC_AND_NAICS',
  FILTER_PEOPLE_BASIC_RELEVANCY = 'FILTER_PEOPLE_BASIC_RELEVANCY',
  FILTER_PEOPLE_BASIC_IN_AND_EXCLUDE_LISTS = 'FILTER_PEOPLE_BASIC_IN_AND_EXCLUDE_LISTS',
  FILTER_PEOPLE_BASIC_IN_AND_EXCLUDE_LABELS = 'FILTER_PEOPLE_BASIC_IN_AND_EXCLUDE_LABELS',
  FILTER_PEOPLE_BASIC_IN_AND_EXCLUDE_RECORD_OWNERSHIP = 'FILTER_PEOPLE_BASIC_IN_AND_EXCLUDE_RECORD_OWNERSHIP',
  FILTER_PEOPLE_BASIC_ENRICHMENT_REQUESTS = 'FILTER_PEOPLE_BASIC_ENRICHMENT_REQUESTS',
  FILTER_PEOPLE_BASIC_EXPORT_REQUESTS = 'FILTER_PEOPLE_BASIC_EXPORT_REQUESTS',
  FILTER_PEOPLE_ADVANCED_CURRENT_AND_PAST_COMPANY = 'FILTER_PEOPLE_ADVANCED_CURRENT_AND_PAST_COMPANY',
  FILTER_PEOPLE_ADVANCED_KEYWORDS = 'FILTER_PEOPLE_ADVANCED_KEYWORDS',
  FILTER_PEOPLE_ADVANCED_SKILLS = 'FILTER_PEOPLE_ADVANCED_SKILLS',
  FILTER_PEOPLE_ADVANCED_EDUCATION = 'FILTER_PEOPLE_ADVANCED_EDUCATION',
  FILTER_PEOPLE_ADVANCED_CERTIFICATIONS = 'FILTER_PEOPLE_ADVANCED_CERTIFICATIONS',
  FILTER_PEOPLE_ADVANCED_LANGUAGE_SKILLS = 'FILTER_PEOPLE_ADVANCED_LANGUAGE_SKILLS',
  FILTER_PEOPLE_ADVANCED_PROFILE_BADGE = 'FILTER_PEOPLE_ADVANCED_PROFILE_BADGE',
  FILTER_PEOPLE_ADVANCED_TECHNOLOGIES = 'FILTER_PEOPLE_ADVANCED_TECHNOLOGIES',
  FILTER_PEOPLE_ADVANCED_PRODUCT_AND_SERVICES = 'FILTER_PEOPLE_ADVANCED_PRODUCT_AND_SERVICES',
  FILTER_PEOPLE_ADVANCED_ANNUAL_REVENUE = 'FILTER_PEOPLE_ADVANCED_ANNUAL_REVENUE',
  FILTER_PEOPLE_ADVANCED_FUNDING = 'FILTER_PEOPLE_ADVANCED_FUNDING',
  FILTER_PEOPLE_ADVANCED_FOUNDED_YEAR = 'FILTER_PEOPLE_ADVANCED_FOUNDED_YEAR',
  FILTER_PEOPLE_ADVANCED_HEADCOUNT_GROWTH = 'FILTER_PEOPLE_ADVANCED_HEADCOUNT_GROWTH',
  FILTER_PEOPLE_ADVANCED_EMPLOYEE_BY_DEPARTMENT = 'FILTER_PEOPLE_ADVANCED_EMPLOYEE_BY_DEPARTMENT',
  FILTER_PEOPLE_ADVANCED_LOCATIONS_WORLD_WIDE = 'FILTER_PEOPLE_ADVANCED_LOCATIONS_WORLD_WIDE',
  FILTER_PEOPLE_ADVANCED_OPERATION_LANGUAGE = 'FILTER_PEOPLE_ADVANCED_OPERATION_LANGUAGE',
  FILTER_PEOPLE_ADVANCED_SOCIAL_MEDIA_CHANNEL = 'FILTER_PEOPLE_ADVANCED_SOCIAL_MEDIA_CHANNEL',
  FILTER_PEOPLE_ADVANCED_SOCIAL_MEDIA_FOLLOWERS = 'FILTER_PEOPLE_ADVANCED_SOCIAL_MEDIA_FOLLOWERS',
  FILTER_PEOPLE_ADVANCED_SIGNAL_AND_ALERTS = 'FILTER_PEOPLE_ADVANCED_SIGNAL_AND_ALERTS',
  FILTER_COMPANY_BASIC_SEARCH = 'FILTER_COMPANY_BASIC_SEARCH',
  FILTER_COMPANY_BASIC_COMPANY_INFO = 'FILTER_COMPANY_BASIC_COMPANY_INFO',
  FILTER_COMPANY_BASIC_LOCATION = 'FILTER_COMPANY_BASIC_LOCATION',
  FILTER_COMPANY_BASIC_LOCATIONS_WORLD_WIDE = 'FILTER_COMPANY_BASIC_LOCATIONS_WORLD_WIDE',
  FILTER_COMPANY_BASIC_INDUSTRY = 'FILTER_COMPANY_BASIC_INDUSTRY',
  FILTER_COMPANY_BASIC_EMPLOYEE_SIZE = 'FILTER_COMPANY_BASIC_EMPLOYEE_SIZE',
  FILTER_COMPANY_BASIC_COMPANY_TYPE = 'FILTER_COMPANY_BASIC_COMPANY_TYPE',
  FILTER_COMPANY_BASIC_FOUNDED_YEAR = 'FILTER_COMPANY_BASIC_FOUNDED_YEAR',
  FILTER_COMPANY_BASIC_KEYWORDS = 'FILTER_COMPANY_BASIC_KEYWORDS',
  FILTER_COMPANY_BASIC_SIC_AND_NAICS = 'FILTER_COMPANY_BASIC_SIC_AND_NAICS',
  FILTER_COMPANY_BASIC_RELEVANCY = 'FILTER_COMPANY_BASIC_RELEVANCY',
  FILTER_COMPANY_BASIC_SOCIAL_MEDIA_CHANNEL = 'FILTER_COMPANY_BASIC_SOCIAL_MEDIA_CHANNEL',
  FILTER_COMPANY_BASIC_SOCIAL_MEDIA_FOLLOWERS = 'FILTER_COMPANY_BASIC_SOCIAL_MEDIA_FOLLOWERS',
  FILTER_COMPANY_BASIC_IN_AND_EXCLUDE_LISTS = 'FILTER_COMPANY_BASIC_IN_AND_EXCLUDE_LISTS',
  FILTER_COMPANY_BASIC_IN_AND_EXCLUDE_LABELS = 'FILTER_COMPANY_BASIC_IN_AND_EXCLUDE_LABELS',
  FILTER_COMPANY_BASIC_IN_AND_EXCLUDE_RECORD_OWNERSHIP = 'FILTER_COMPANY_BASIC_IN_AND_EXCLUDE_RECORD_OWNERSHIP',
  FILTER_COMPANY_BASIC_ENRICHMENT_REQUESTS = 'FILTER_COMPANY_BASIC_ENRICHMENT_REQUESTS',
  FILTER_COMPANY_BASIC_EXPORT_REQUESTS = 'FILTER_COMPANY_BASIC_EXPORT_REQUESTS',
  FILTER_COMPANY_ADVANCED_ANNUAL_REVENUE = 'FILTER_COMPANY_ADVANCED_ANNUAL_REVENUE',
  FILTER_COMPANY_ADVANCED_FUNDING = 'FILTER_COMPANY_ADVANCED_FUNDING',
  FILTER_COMPANY_ADVANCED_HEADCOUNT_GROWTH = 'FILTER_COMPANY_ADVANCED_HEADCOUNT_GROWTH',
  FILTER_COMPANY_ADVANCED_EMPLOYEE_BY_DEPARTMENT = 'FILTER_COMPANY_ADVANCED_EMPLOYEE_BY_DEPARTMENT',
  FILTER_COMPANY_ADVANCED_TECHNOLOGIES = 'FILTER_COMPANY_ADVANCED_TECHNOLOGIES',
  FILTER_COMPANY_ADVANCED_PRODUCT_AND_SERVICES = 'FILTER_COMPANY_ADVANCED_PRODUCT_AND_SERVICES',
  FILTER_COMPANY_ADVANCED_OPERATION_LANGUAGE = 'FILTER_COMPANY_ADVANCED_OPERATION_LANGUAGE',
  FILTER_COMPANY_ADVANCED_SIGNAL_AND_ALERTS = 'FILTER_COMPANY_ADVANCED_SIGNAL_AND_ALERTS',
  CSV_ENRICHMENT_PEOPLE_DETAIL = 'CSV_ENRICHMENT_PEOPLE_DETAIL',
  CSV_ENRICHMENT_COMPANY_DETAIL = 'CSV_ENRICHMENT_COMPANY_DETAIL',
  CSV_ENRICHMENT_EMAIL = 'CSV_ENRICHMENT_EMAIL',
  CSV_EXPORT_ALL_DATA = 'CSV_EXPORT_ALL_DATA',
  CSV_EXPORT_LINKEDIN_AUDIENCE = 'CSV_EXPORT_LINKEDIN_AUDIENCE',
  CSV_EXPORT_SALES_NAVIGATOR = 'CSV_EXPORT_SALES_NAVIGATOR',
  INTEGRATION_SMARTLEAD = 'INTEGRATION_SMARTLEAD',
  INTEGRATION_ZAPIER = 'INTEGRATION_ZAPIER',
  INTEGRATION_OUTREACH = 'INTEGRATION_OUTREACH',
  INTEGRATION_SALESLOFT = 'INTEGRATION_SALESLOFT',
  INTEGRATION_CLAY = 'INTEGRATION_CLAY',
  AI_PERSONALITY_ANALYSIS = 'AI_PERSONALITY_ANALYSIS',
  AI_COMPANY_LOOKALIKE = 'AI_COMPANY_LOOKALIKE',
  AI_PEOPLE_LOOKALIKE = 'AI_PEOPLE_LOOKALIKE',
  AI_BUYING_TEAM = 'AI_BUYING_TEAM',
  API_ACCESS = 'API_ACCESS',
  API_CALL_RATE = 'API_CALL_RATE',
  CREDITS_ROLLOVER = 'CREDITS_ROLLOVER',
  REALTIME_CATCH_ALL_EMAILS = 'REALTIME_CATCH_ALL_EMAILS',
  REALTIME_SMTP_EMAILS = 'REALTIME_SMTP_EMAILS',
  REALTIME_VERIFIED_EMAILS = 'REALTIME_VERIFIED_EMAILS',
  VALID_MOBILE_NUMBERS = 'VALID_MOBILE_NUMBERS',
  DATA_FRESHNESS_30_DAY = 'DATA_FRESHNESS_30_DAY',
  GLOBAL_COVERAGE = 'GLOBAL_COVERAGE',
  WHITE_LABEL = 'WHITE_LABEL',
  SEATS = 'SEATS',
  VIEW_LIMIT = 'VIEW_LIMIT',
  EXPORT_SELECTION_LIMIT = 'EXPORT_SELECTION_LIMIT',
}

export type PlanEntitlementMetadata = EntitlementMetadata<PlanCapability>;
export type PlanEntitlement = Entitlement<PlanCapability>;
export type PlanSubscription = Subscription<PlanCapability>;
